import { Layer as DeckGLLayer } from "@deck.gl/core"
import {
    AnyAction,
    KeyboardShortcut,
    ToolName,
    UploadComponentProps,
    AddOns,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
} from "@nova/constants"
import { ModalNames } from "features/modals/types"
import React, { FC, FunctionComponent, ReactElement, ReactNode, type JSX } from "react"
import { RootState } from "integrations/redux"
import { Author, Layer, MessageFns, NovaEntity } from "@nova/protobuf"
import { withRoot } from "@nova/with-root"
import { ActionCreatorWithPayload, createSelector, createSlice } from "@reduxjs/toolkit"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { deactivateFire } from "features/map/control/slice"
import { NovaMarkerAnchor } from "features/map/NovaMarker"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { registerIconPlugin } from "./Toolbox/Icons/IconPlugin"
import { registerLinePlugin } from "./Toolbox/Line/LinePlugin"
import { registerGroupPlugin } from "./Toolbox/Groups/GroupPlugin"
import { registerUserLocationSharePlugin } from "./UserLocationShare/UserLocationSharePlugin"
import { registerInfraredImagePlugin } from "./InfraredImage/InfraredImagePlugin"
import { registerOrthomosaicPlugin } from "./Orthomosaic/OrthomosaicPlugin"
import { registerSatellitePlugin } from "./Satellite/SatellitePlugin"
import {
    getGenericAssetsCategoryHeader,
    getGenericAssetsListItem,
} from "panels/content/side-panel/generics/genericAssetsListItems"
import { registerTextPlugin } from "./Toolbox/Text/TextPlugin"
import { registerPerimeterPlugin } from "./DrawPerimeter/DrawPerimeterPlugin"
import { registerVideoPlugin } from "./Video/FMV/VideoPlugin"
import { registerPanoramaPlugin } from "./Panorama/PanoramaPlugin"
import { registerRulerPlugin } from "./Toolbox/Ruler/RulerPlugin"
import { registerLiveVideoPlugin } from "./Video/LiveVideo/LiveVideoPlugin"
import { getGenericInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import {
    getGenericInspectorPanelHeader,
    getGenericUseHeaderActions,
    InspectorPanelAction,
} from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { getGenericInspectorPanelBody } from "features/map/inspector-panel/inspector-panels/bodies/GenericInspectorPanelBody"
import {
    getGenericInspectorPanelFooter,
    getGenericUseFooterActions,
    InspectorPanelFooterAction,
} from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import { InspectorPanelPlugin as InspectorPanelPluginData } from "features/map/inspector-panel/useInspectorPanel"
import { registerPdfPlugin } from "./PDFv2/PDFPlugin"
import { getGenericPdfListItem } from "panels/content/side-panel/generics/GenericPdfListItem"
import {
    getGenericUseSidePanelEntities,
    useGenericSidePanelEntityMap,
} from "panels/content/side-panel/generics/genericUseSidePanelEntities"
import { getUseAssetsContextMenuSections } from "panels/content/side-panel/generics/assetsContextMenuSections"
import { getUsePdfContextMenuSections } from "panels/content/side-panel/generics/pdfContextMenuSections"
import { getUseLayersContextMenuSections } from "panels/content/side-panel/generics/layerContextMenuSections"
import { registerMergedHotspotsPlugin } from "./InfraredImage/MergedHotspotsPlugin"
import { registerColorFinderPlugin } from "./ColorFinder/VideoColorFinder/ColorFinderPlugin"
import { StatusAndProgressData } from "panels/content/side-panel/generics/GenericLayerListItemProgress"
import { registerPDFRasterPlugin } from "./PDFRaster/PDFRasterPlugin"
import { SyncThunk } from "integrations/redux/types"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { getSidePanelFeatureFromLayerType } from "panels/content/side-panel/constants"
import store from "integrations/redux/store"
import { LocationResult } from "features/map/search/types"
import { SidePanelActiveFeaturePlugin as SidePanelActiveFeaturePluginBase } from "panels/content/side-panel/useSidePanelActiveFeature"
import { registerCommentPlugin } from "./Toolbox/MapComments/CommentPlugin"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PluginSpec<T extends NovaEntity = any> = {
    name: string
    displayName: string
    mockedDisplayName?: string
    actionsDisplayName?: string
    sidePanelLayerType: SidePanelLayerType
    PrimaryIcon: FC
    SecondaryIcon?: FC
    ImageIconUrl?: string
    updateAction:
        | ActionCreatorWithPayload<{
              id: string
              updates: Partial<T>
              context?: Record<string, unknown>
          }>
        | SyncThunk<{
              id: string
              updates: Partial<T>
              context?: Record<string, unknown>
          }>
    deleteAction:
        | ActionCreatorWithPayload<{ id: string; context?: Record<string, unknown> }>
        | SyncThunk<{ id: string; context?: Record<string, unknown> }>
    confirmDelete: (entity: T) => boolean
    protobufMessage: MessageFns<T>
    selectSingleEntity: (id: string) => (s: RootState) => T
    selectAllEntities: (s: RootState) => T[]
    useUploadStatusAndProgress?: (id: string) => StatusAndProgressData
    zoomTo: (entity: T) => void
    duplicate?: (entity: T) => void
    search?: (input: string, entity: T) => string | undefined
}

export type LayerPluginSpec<T extends Layer = Layer> = PluginSpec<T>

type SidePanelLayersPlugin = {
    type: "side-panel-layers-plugin"
    // accept: "Layer" // TODO CONNOR: This breaks PDF rasters
    sidePanelLayerType: SidePanelLayerType
    categoryName: string
    filterKey: string | undefined
    useSidePanelEntities: () => {
        id: string
        index: string
        parentId?: string
        archivedTimestamp?: number
    }[]
    SidePanelListItem: FC<{ id: string; isHovered: boolean }>
    useContextMenuSections: (
        id: string,
        sortedEntities: SidePanelLayerOrGroup[]
    ) => ContextMenuItem[][]
}

type SidePanelStickyLayersPlugin = {
    type: "side-panel-sticky-layers-plugin"
    accept: "Layer"
    filterKey: string | undefined
    useSidePanelEntities: () => { id: string; index: string; archivedTimestamp?: number }[]
    useSidePanelEntityMap: (ids: string[]) => Record<string, SidePanelLayerOrGroup>
    SidePanelListItem: FC<{ id: string; isHovered: boolean }>
    useContextMenuSections: (
        id: string,
        sortedEntities: SidePanelLayerOrGroup[]
    ) => ContextMenuItem[][]
}

type TopBarFlyoutLayersPlugin = {
    type: "top-menu-flyout-layers-plugin"
    accept: "Layer"
    flyoutId: string
    useEntities: () => { id: string }[]
    menuConfig: {
        TriggerComponent: FC<{ entityCount: number; open: boolean }>
        MenuComponent: FC<{ children: ReactNode }>
    } | null
    LayerListItem: FC<{ id: string; isHovered: boolean }>
    useContextMenuSections: (id: string) => ContextMenuItem[][]
}

type SidePanelAssetsPlugin = {
    type: "side-panel-assets-plugin"
    accept: "Layer"
    categoryId: string
    useSidePanelEntities: () => { id: string; archivedTimestamp?: number }[]
    SidePanelCategoryHeader: React.FC<{ entityCount: number }>
    useThisCategoryHeader: boolean
    SidePanelListItem: React.FC<{ id: string }>
    useContextMenuSections: (id: string) => ContextMenuItem[][]
}

type SidePanelPdfPlugin = {
    type: "side-panel-pdf-plugin"
    // accept: "Any", // TODO CONNOR
    useSidePanelEntities: () => { id: string; index: string; archivedTimestamp?: number }[]
    SidePanelListItem: FC<{ id: string; isHovered: boolean }>
    useContextMenuSections: (
        id: string,
        sortedEntities: SidePanelLayerOrGroup[]
    ) => ContextMenuItem[][]
}

type SidePanelActiveFeaturePlugin = {
    type: "side-panel-active-feature-plugin"
    selectSingleEntity: PluginSpec["selectSingleEntity"]
    featureId: NovaPluginIds
    SidePanelComponent: FC<{ id?: string; plugin: SidePanelActiveFeaturePluginBase }>
}

type InspectorPanelPlugin = {
    type: "inspector-panel-plugin"
    sidePanelLayerType: SidePanelLayerType
    InspectorPanelComponent: FC<{ id: string | undefined; plugin: InspectorPanelPluginData }>
    useHeaderActions: (id: string) => InspectorPanelAction[]
    useFooterActions: (id: string) => InspectorPanelFooterAction[]
    useSubtitles: (id: string) => string[] | ReactElement
}

export type ToolboxPlugin = {
    type: "toolbox-plugin"
    name: ToolName
    icon: FC
    shortcut?: KeyboardShortcut
    viewerAllowed?: boolean
    submitHandler?: (...args: never[]) => unknown
    portalComponent?: FunctionComponent
    // something complained when I used the FC type and using () => ReactElement fixed it
    TutorialComponent?: FunctionComponent
}

export type FeaturePlugin = {
    type: "feature-plugin"
    featureId: NovaPluginIds
    displayName: string
    iconUrl: string
    // paid addOn needed to use this plugin
    subscriptionType?: AddOns
    sampleDataModalName?: ModalNames
    // component to render in the main area of the side panel
    component?: (props: React.HTMLAttributes<unknown>) => JSX.Element | null
    sidePanelPluginActionsComponent?: ({ onBlur }: { onBlur: () => void }) => JSX.Element | null
    uploadComponent?: (props: UploadComponentProps) => JSX.Element | null
} & (
    | {
          createAction?: (payload: {
              data: {
                  createdBy: Author
                  skippedProcessing: boolean
              }
          }) => AnyAction
          onClick?: never
      }
    | { createAction?: never; onClick?: () => void }
)

export type MapLayerPlugin = {
    type: "map-layer-plugin"
    useLayer: () => DeckGLLayer | DeckGLLayer[]
    zIndex?: number
}

export type ModalPlugin = {
    type: "map-modal-plugin"
    Modal: FC
}

export type UIController = {
    type: "ui-controller"
    name: string
    Controller: FC
}

export type BoundingBoxDataProvider = {
    type: "bounding-box-data-provider"
    useBoundingBoxData: () => BoundingBoxLayerData[]
    showLabel: boolean
}

export type MapNodeAnchor = NovaMarkerAnchor

export type MapNodeIconState = "active" | "hover" | "default"

export type MapNodeIconProps = { state: MapNodeIconState }

export type MapNodeIcon =
    | { kind: "static"; icons: Partial<Record<MapNodeIconState, string>> & { default: string } }
    | { kind: "render"; Component: FC<MapNodeIconProps> }

export const staticIcon = (
    icons: Partial<Record<MapNodeIconState, string>> & { default: string }
): MapNodeIcon => ({ kind: "static", icons })

export const renderedIcon = (Component: FC<MapNodeIconProps>): MapNodeIcon => ({
    kind: "render",
    Component,
})

export type MapNodePlugin = {
    type: "map-node-plugin"
    useMapNodes: () => {
        id: string
        position: { lng: number; lat: number }
        visible: boolean
        locked: boolean
        active: boolean
        onClick: () => void
        iconSrc: MapNodeIcon
        anchor?: MapNodeAnchor
        draggable?: boolean
        onDragEnd?: (position: { lat: number; lng: number }) => void
        interactiveWhileCommenting?: boolean
    }[]
}

const pluginMap: { [K in AnyPlugin["type"]]: FactoryFor<K> } = {
    "side-panel-layers-plugin": {
        sidePanelLayerType: (spec) => spec.sidePanelLayerType,
        categoryName: (spec) => spec.displayName,
        filterKey: (spec) => spec.displayName,
        useSidePanelEntities: (spec) => getGenericUseSidePanelEntities(spec),
        SidePanelListItem: (spec) => getGenericLayerListItem({ spec }),
        useContextMenuSections: (spec) => getUseLayersContextMenuSections(spec),
    },
    "side-panel-sticky-layers-plugin": {
        filterKey: (spec) => spec.displayName,
        useSidePanelEntities: (spec) => getGenericUseSidePanelEntities(spec),
        useSidePanelEntityMap: () => useGenericSidePanelEntityMap,
        SidePanelListItem: (spec) => getGenericLayerListItem({ spec }),
        useContextMenuSections: (spec) => getUseLayersContextMenuSections(spec),
    },
    "top-menu-flyout-layers-plugin": {
        flyoutId: (spec) => spec.name,
        useEntities: (spec) => getGenericUseSidePanelEntities(spec),
        menuConfig: () => null,
        LayerListItem: (spec) => getGenericLayerListItem({ spec }),
        useContextMenuSections: (spec) => getUseAssetsContextMenuSections(spec),
    },
    "side-panel-assets-plugin": {
        categoryId: (spec) => spec.name,
        useSidePanelEntities: (spec) => getGenericUseSidePanelEntities(spec),
        SidePanelCategoryHeader: (spec) => getGenericAssetsCategoryHeader(spec),
        useThisCategoryHeader: () => true,
        SidePanelListItem: (spec) => getGenericAssetsListItem({ spec }),
        useContextMenuSections: (spec) => getUseAssetsContextMenuSections(spec),
    },
    "side-panel-pdf-plugin": {
        useSidePanelEntities: (spec) => getGenericUseSidePanelEntities(spec),
        SidePanelListItem: (spec) => getGenericPdfListItem({ spec }),
        useContextMenuSections: (spec) => getUsePdfContextMenuSections(spec),
    },
    "side-panel-active-feature-plugin": {
        selectSingleEntity: (spec) => spec.selectSingleEntity,
        featureId: (spec) => getSidePanelFeatureFromLayerType(spec.sidePanelLayerType)!,
        SidePanelComponent: () => () => null,
    },
    "inspector-panel-plugin": {
        sidePanelLayerType: (spec) => spec.sidePanelLayerType,
        InspectorPanelComponent: (spec) =>
            getGenericInspectorPanelComponent({
                spec,
                Header: getGenericInspectorPanelHeader(spec),
                Body: getGenericInspectorPanelBody({}),
                Footer: getGenericInspectorPanelFooter(),
            }),
        useHeaderActions: (spec) => getGenericUseHeaderActions(spec),
        useFooterActions: (spec) => getGenericUseFooterActions(spec),
        useSubtitles: () => () => [],
    },
    "bounding-box-data-provider": {
        useBoundingBoxData: () => () => [],
        showLabel: () => false,
    },
    "feature-plugin": {
        iconUrl: (spec) => spec.ImageIconUrl || "",
        featureId: (spec) => getSidePanelFeatureFromLayerType(spec.sidePanelLayerType)!,
        displayName: (spec) => spec.displayName,
    },
    "map-layer-plugin": {
        useLayer: () => () => [],
    },
    "toolbox-plugin": { name: () => ToolName.Create, icon: (spec) => spec.PrimaryIcon },
    "map-modal-plugin": { Modal: () => () => null },
    "ui-controller": { name: (spec) => spec.name, Controller: () => () => null },
    "map-node-plugin": { useMapNodes: () => () => [] },
}

type AnyPlugin =
    | SidePanelLayersPlugin
    | SidePanelStickyLayersPlugin
    | TopBarFlyoutLayersPlugin
    | SidePanelAssetsPlugin
    | SidePanelPdfPlugin
    | SidePanelActiveFeaturePlugin
    | InspectorPanelPlugin
    | BoundingBoxDataProvider
    | FeaturePlugin
    | MapLayerPlugin
    | ToolboxPlugin
    | ModalPlugin
    | UIController
    | MapNodePlugin

type AcceptSpec<N extends AnyPlugin["type"]> =
    Extract<AnyPlugin, { type: N }> extends { accept?: infer A }
        ? A extends "Layer"
            ? LayerPluginSpec
            : never
        : PluginSpec

type BaseFor<N extends AnyPlugin["type"]> = {
    [P in Exclude<keyof Extract<AnyPlugin, { type: N }>, "type" | "accept">]: Extract<
        AnyPlugin,
        { type: N }
    >[P]
}

type FactoryFor<N extends AnyPlugin["type"]> = {
    [P in keyof BaseFor<N> as undefined extends BaseFor<N>[P] ? never : P]: (
        spec: AcceptSpec<N>
    ) => BaseFor<N>[P]
} & {
    [P in keyof BaseFor<N> as undefined extends BaseFor<N>[P] ? P : never]?: (
        spec: AcceptSpec<N>
    ) => Exclude<BaseFor<N>[P], undefined>
}

type SpecFor<T extends NovaEntity> = T extends Layer
    ? LayerPluginSpec & PluginSpec<T>
    : PluginSpec<T>

export class PluginRegistry {
    specs: Record<string, PluginSpec> = {}
    plugins = Object.keys(pluginMap).reduce(
        (acc, cur) => {
            acc[cur as AnyPlugin["type"]] = []
            return acc
        },
        {} as {
            [K in AnyPlugin["type"]]: BaseFor<K>[]
        }
    )

    register<T extends NovaEntity>(spec: PluginSpec<T>) {
        this.specs[spec.name] = spec
        const plugins = this.plugins
        const functionMap = {
            implement<N extends AnyPlugin["type"]>(
                typeCheck: N & (SpecFor<T> extends AcceptSpec<N> ? N : never),
                getOverrides: (
                    spec: PluginSpec<T>
                ) => Partial<Omit<Extract<AnyPlugin, { type: N }>, "type" | "accept">> = () => ({})
            ) {
                const type = typeCheck as N
                const factory = pluginMap[type] as FactoryFor<N>

                const base = {} as BaseFor<N>
                for (const k in factory) {
                    const key = k as keyof FactoryFor<N>
                    base[key as keyof typeof base] = factory[key](spec as AcceptSpec<N>)
                }

                Object.assign(base, getOverrides(spec))

                plugins[type].push(base)
                return functionMap
            },
        }
        return functionMap
    }

    search(input: string) {
        return Object.values(this.specs)
            .map((spec) => {
                const state = store.getState()
                const entities = spec
                    .selectAllEntities(state)
                    .filter((entity) => !entity.archivedTimestamp)
                return entities.reduce<LocationResult[]>((acc, entity) => {
                    const searchResult = spec.search?.(input, entity)
                    if (searchResult) {
                        acc.push({
                            title: searchResult,
                            Icon: spec.PrimaryIcon,
                            iconSize: "normal",
                            zoomTo: () => spec.zoomTo(entity),
                        })
                    }
                    return acc
                }, [])
            })
            .flat()
    }

    zoomToEntity(entity: SidePanelLayerOrGroup) {
        if (!entity.type) return
        Object.values(this.specs)
            .find((spec) => spec.sidePanelLayerType === entity.type)
            ?.zoomTo(entity)
    }

    getSidePanelLayerTypeToPluginNameMap() {
        return Object.fromEntries(
            Object.values(this.specs).map((spec) => {
                return [spec.sidePanelLayerType, spec.name]
            })
        ) as Partial<Record<SidePanelLayerType, string>>
    }

    getSidePanelLayerTypeToUpdateFunctionMap() {
        return Object.fromEntries(
            Object.values(this.specs).map((spec) => {
                return [spec.sidePanelLayerType, spec.updateAction]
            })
        )
    }

    getSidePanelLayerTypeToDeleteFunctionMap() {
        return Object.fromEntries(
            Object.values(this.specs).map((spec) => {
                return [spec.sidePanelLayerType, spec.deleteAction]
            })
        )
    }

    getSidePanelLayerTypeToPrimaryIconMap() {
        return Object.fromEntries(
            Object.values(this.specs).map((spec) => {
                return [spec.sidePanelLayerType, spec.PrimaryIcon]
            })
        )
    }

    get<N extends AnyPlugin["type"]>(type: N) {
        return this.plugins[type]
    }
}

let cachedRegistry: PluginRegistry | null = null

function registry() {
    if (cachedRegistry) return cachedRegistry
    const registry = new PluginRegistry()
    // iterate through plugins and register them all
    // @todo - when plugins that you need permission for come in, need to check permissions here

    registerOrthomosaicPlugin(registry)
    registerInfraredImagePlugin(registry)
    registerMergedHotspotsPlugin(registry)
    registerPerimeterPlugin(registry)
    registerColorFinderPlugin(registry)
    registerLiveVideoPlugin(registry)
    registerVideoPlugin(registry)
    registerPanoramaPlugin(registry)
    registerSatellitePlugin(registry)

    registerIconPlugin(registry)
    registerLinePlugin(registry)
    registerTextPlugin(registry)
    registerRulerPlugin(registry)
    registerCommentPlugin(registry)
    registerPDFRasterPlugin(registry)
    registerPdfPlugin(registry)

    registerGroupPlugin(registry)
    registerUserLocationSharePlugin(registry)

    cachedRegistry = registry
    return cachedRegistry
}

export function getToolboxPlugins() {
    return registry().get("toolbox-plugin")
}

export function getFeaturePlugins() {
    return registry().get("feature-plugin")
}

export function getMapLayerPlugins() {
    return registry()
        .get("map-layer-plugin")
        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
}

export function getUIControllers() {
    return registry().get("ui-controller")
}

export function getMapModals() {
    return registry().get("map-modal-plugin")
}

export function getBoundingBoxDataProviders() {
    return registry().get("bounding-box-data-provider")
}

export function getSidePanelLayersPlugins() {
    return registry().get("side-panel-layers-plugin")
}
export function getSidePanelStickyLayersPlugins() {
    return registry().get("side-panel-sticky-layers-plugin")
}
export function getTopMenuFlyoutLayersPlugins() {
    return registry().get("top-menu-flyout-layers-plugin")
}
export function getSidePanelAssetsPlugins() {
    return registry().get("side-panel-assets-plugin")
}
export function getSidePanelPdfPlugins() {
    return registry().get("side-panel-pdf-plugin")
}
export function getSidePanelActiveFeaturePlugins() {
    return registry().get("side-panel-active-feature-plugin")
}
export function getInspectorPanelPlugins() {
    return registry().get("inspector-panel-plugin")
}
export function getMapNodePlugins() {
    return registry().get("map-node-plugin")
}

export function searchRegisteredEntities(input: string) {
    return registry().search(input)
}
export function zoomToEntity(entity: SidePanelLayerOrGroup) {
    return registry().zoomToEntity(entity)
}
export function getSidePanelLayerTypeToPluginNameMap() {
    return registry().getSidePanelLayerTypeToPluginNameMap()
}
export function getSidePanelLayerTypeToUpdateFunctionMap() {
    return registry().getSidePanelLayerTypeToUpdateFunctionMap()
}
export function getSidePanelLayerTypeToDeleteFunctionMap() {
    return registry().getSidePanelLayerTypeToDeleteFunctionMap()
}
export function getSidePanelLayerTypeToPrimaryIconMap() {
    return registry().getSidePanelLayerTypeToPrimaryIconMap()
}

const uiStateName = "plugin-registry-UI"
interface PluginUIState {
    active: string | undefined
    timestamp: string | undefined
    open: boolean
    centerPlugin: number
}

const initialState = {
    active: undefined as string | undefined,
    timestamp: undefined as string | undefined,
    open: true as boolean,
    centerPlugin: 6,
}

const {
    reducer: uiStateReducer,
    actions: { setPanelVisibility, setCenterPlugin },
} = createSlice({
    name: uiStateName,
    initialState: { ...initialState },
    reducers: {
        setPanelVisibility: (state, action: { payload: boolean }) => {
            state.open = action.payload
        },
        setCenterPlugin: (state, action: { payload: number }) => {
            state.centerPlugin = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(deactivateFire, () => {
            return { ...initialState }
        })
    },
})

const { selectTimestamp, selectPanelVisibility, selectActivePlugin, selectCenterPlugin } = withRoot(
    (r: Record<typeof uiStateName, PluginUIState>) => r["plugin-registry-UI"],
    {
        selectTimestamp: () => (s) => (s.active ? s.timestamp : undefined),
        selectPanelVisibility: () => (s) => s.open,
        selectActivePlugin: () => (s) => s.active,
        selectCenterPlugin: () => (s) => s.centerPlugin,
    }
)

// actions
export { setCenterPlugin, setPanelVisibility }

// selectors
export { selectActivePlugin, selectCenterPlugin, selectPanelVisibility, selectTimestamp }

// reducer
export const reducer = {
    [uiStateName]: uiStateReducer,
}

export const selectActiveToolSpec = createSelector(
    [(s: RootState) => s["toolbox-registry-UI"].active],
    (toolName) => getToolboxPlugins().find((plugin) => plugin.name === toolName)
)
